import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as PR from "../../prime-modules/index";
import AdminFooter from "../layout/admin-footer";
import AdminHeader from "../layout/admin-header";
import { globalConfig } from '../../GlobalConfig';
import { useSelector, useDispatch } from 'react-redux';
import { getCountriesList, getWholesaleList, updateWholesalePrices, getPriceFilters } from "../../services/api.jsx";
import "./WholesalePriceAdjustment.scss";
import * as utils from '../../utils';
import { getCurrentTimestamp } from "../../utils/reuse.js";
import RoamingColumn from "../prepaid-retail-vouchers/RoamingInfo.jsx";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";
import AllowanceInfo from "../price-adjustment/AllowanceInfo";

const createEditCatalogItem = (data) => {
    const catalogId = data.catalogId || data.name;
    const overrideVal = (data?.providerPriceOverride !== undefined && data?.providerPriceOverride !== null)
        ? data.providerPriceOverride
        : null;
    return {
        name: data.name,
        providerPrice: data.providerPrice,
        providerPriceOverride: overrideVal,
        editProviderPriceOverride: overrideVal,
        ourPrice: data.ourPrice,
        editOurPrice: data.ourPrice,
        offered: Boolean(data.offered),
        editOffered: Boolean(data.offered),
        error: '',
        minRetailPrice: data?.minRetailPrice || null,
        providerName: data?.providerName,
        catalogId,
    };
};

const WholesalePriceAdjustment = () => {
    const appName = process.env?.REACT_APP_NAME;
    const timeStamp = getCurrentTimestamp();
    const dispatch = useDispatch();
    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);
    const toast = useRef();

    const [catalogs, setCatalogs] = useState([]);
    const [editCatalogs, setEditCatalogs] = useState({});

    const [sortField, setSortField] = useState('providerPrice');
    const [sortOrder, setSortOrder] = useState(1);

    const [uniqueRegions, setUniqueRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(null);

    const [allCountries, setAllCountries] = useState([]);
    const [countries, setCountries] = useState([]);
    const [country, setCountry] = useState('GB');
    const [loading, setLoading] = useState(true);

    const [filtersVisible, setFiltersVisible] = useState(false);

    const [providerList, setProviderList] = useState([]);
    const [durationList, setDurationList] = useState([]);
    const [dataAmountList, setDataAmountList] = useState([]);

    const [selectedProviders, setSelectedProviders] = useState([]);
    const [selectedDurations, setSelectedDurations] = useState([]);
    const [selectedDataAmounts, setSelectedDataAmounts] = useState([]);
    const [isChanged, setIsChanged] = useState(false);

    const [filterSelectedRegion, setFilterSelectedRegion] = useState(null);

    const [filterCountries, setFilterCountries] = useState([]);
    const [filterCountry, setFilterCountry] = useState('GB');

    const [filters, setFilters] = useState({});

    const [filterOpen, setFilterOpen] = useState(false);
    const toggleFilter = () => setFilterOpen(!filterOpen);

    const setDataAmount = (rowData) => {
        return (rowData.dataAmountForDisplay) ? rowData.dataAmountForDisplay : ((rowData.dataAmount / 1000) + (globalConfig.dataSize));
    };

    const setDataDuration = (rowData) => {
        return (rowData.duration && (rowData.duration) + " Days");
    };

    const setDataSpeed = (rowData) => {
        return (rowData.speed && (rowData.speed.join('/')));
    };

    const removePriceErrors = useCallback(() => {
        setCatalogs((prevCatalogs) =>
            prevCatalogs.map((catalog) => {
                const { errorMsg, ...rest } = catalog;
                return rest;
            })
        );
    }, []);

    const canManagePrices = useMemo(() => {
        return adminData?.permission === undefined ? true : hasPermission(adminData?.permission, PERMISSIONS.MANAGE_PRICES);
    }, [adminData?.permission]);

    const [showOnlyOverridden, setShowOnlyOverridden] = useState(false);

    const displayedCatalogs = useMemo(() => {
        let result = catalogs.map(catalog => {
            const catalogKey = catalog.catalogId || catalog.name;
            const editData = editCatalogs[catalogKey] || {};
            const overrideVal = editData.editProviderPriceOverride !== undefined ? editData.editProviderPriceOverride : (catalog.providerPriceOverride ?? null);
            return {
                ...catalog,
                origProviderPriceOverride: catalog.providerPriceOverride ?? null,
                providerPriceOverride: overrideVal,
                editOurPrice: editData.editOurPrice !== undefined ? editData.editOurPrice : catalog.ourPrice,
                editProviderPriceOverride: overrideVal,
            };
        });

        if (showOnlyOverridden) {
            result = result.filter(item => {
                const origVal = item.origProviderPriceOverride;
                const origHasOverride = origVal !== null && origVal !== undefined && origVal !== "";
                const editVal = item.editProviderPriceOverride;
                const editHasOverride = editVal !== null && editVal !== undefined && editVal !== "";
                return origHasOverride || editHasOverride;
            });
        }

        return result;
    }, [catalogs, editCatalogs, showOnlyOverridden]);

    const getCatalogs = useCallback(async (sortBy = "providerPrice", sortDirection = "asc") => {
        setLoading(true);
        const isFESortOnly = sortBy === 'calculatedPrice';
        const apiSortBy = isFESortOnly ? 'providerPrice' : sortBy;
        const defaultFilters = {
            sortingOrder: sortDirection,
            orderBy: apiSortBy,
            country,
        };
        const queryParams = utils.serializeParams({ ...filters, ...defaultFilters });
        
        const handleResponse = (response) => {
            setLoading(false);
            if (response.result === "SUCCESS") {
                const catalogData = response.data || [];
                let catalogList = [];
                const editCatalogsData = {};
                if (Array.isArray(catalogData) && catalogData.length > 0) {
                    catalogList = catalogData.map((c, index) => {
                        const data = { ...c };
                        data.catalogId = c.name || `catalog_${index}`;
                        return data;
                    });
                    catalogList.forEach(c => {
                        editCatalogsData[c.catalogId] = createEditCatalogItem(c);
                    });
                } else {
                    toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'No plans for the selected country' });
                }
                setCatalogs(catalogList);
                setEditCatalogs(editCatalogsData);
            } else {
                const error = response?.error;
                if (!isFESortOnly) {
                    toast.current?.show({ severity: error?.severity || 'error', summary: 'Error', detail: error?.errorMsg || error?.summary });
                }
            }
        };

        getWholesaleList(queryParams, headers, dispatch, handleResponse);
    }, [country, dispatch, filters, headers]);

    useEffect(() => {
        const handleCountriesResponse = (response) => {
            if (response.result === "SUCCESS") {
                const countriesData = response.data || [];
                if (countriesData.length > 0) {
                    const countriesList = countriesData.map(({ name, region, iso }) => ({
                        name: name.split('(')[0].trim(),
                        region,
                        iso
                    }));
                    setAllCountries(countriesList);
                    setCountries(countriesList);
                    setFilterCountries(countriesList);
                    const uniqueRegionList = [...new Set(countriesList.map(item => item.region))].map(region => ({ label: region, value: region }));
                    const unemptyRegionList = uniqueRegionList.filter(region => region.label && region.value).sort((a, b) => a.label.localeCompare(b.label));
                    setUniqueRegions(unemptyRegionList);
                }
            }
        };
        getCountriesList(headers, dispatch, handleCountriesResponse);
    }, [dispatch, headers]);

    useEffect(() => {
        getCatalogs();
    }, [getCatalogs]);

    useEffect(() => {
        setIsChanged(
            filterSelectedRegion ||
            filterCountry ||
            selectedProviders.length > 0 ||
            selectedDurations.length > 0 ||
            selectedDataAmounts.length > 0
        );
    }, [selectedProviders, selectedDurations, selectedDataAmounts, filterSelectedRegion, filterCountry]);

    const handleRegionSelection = (region) => {
        setSelectedRegion(region);
        setFilterSelectedRegion(region);
        const countriesByRegion = region ? allCountries?.filter(c => c.region === region) : allCountries;
        setCountries(countriesByRegion);
        setFilterCountries(countriesByRegion);
        const countryDisplay = countriesByRegion?.find(c => c.iso === country) ? country : null;
        setCountry(region ? countryDisplay : 'GB');
        setFilters(prev => ({ ...prev, region: region }));
    };

    const handleCountrySelection = (e) => {
        const selectedCountry = e.value;
        setCountry(selectedCountry);
        setFilterCountry(selectedCountry);
        setFilters(prev => ({ ...prev, region: selectedRegion, country: selectedCountry }));
    };

    const regionFilterHandler = (region) => {
        setFilterSelectedRegion(region);
        const countriesByRegion = region ? allCountries?.filter(c => c.region === region) : allCountries;
        const countryDisplay = countriesByRegion?.find(c => c.iso === filterCountry) ? filterCountry : null;
        setFilterCountry(region ? countryDisplay : 'GB');
        setFilterCountries(countriesByRegion);
    };

    const countryFilterHandler = (e) => {
        setFilterCountry(e.value);
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setFiltersVisible(false);
        setCountries(filterCountries);
        const countryDisplay = filterCountries?.find(c => c.iso === filterCountry) ? filterCountry : null;
        setCountry(countryDisplay);
        setSelectedRegion(filterSelectedRegion);
        setFilters(prev => ({
            ...prev,
            region: filterSelectedRegion,
            country: filterCountry,
            providerName: selectedProviders.includes('All') ? undefined : selectedProviders,
            planDuration: selectedDurations,
            dataAmountForDisplay: selectedDataAmounts
        }));
    };

    const resetData = () => {
        const editCatalogsData = {};
        catalogs.forEach(c => {
            const catalogKey = c.catalogId || c.name;
            editCatalogsData[catalogKey] = createEditCatalogItem(c);
        });
        setEditCatalogs(editCatalogsData);
        removePriceErrors();
        setSelectedRegion(null);
        setCountries(allCountries);
        setCountry('GB');
        setFilterSelectedRegion(null);
        setFilters({});
        setShowOnlyOverridden(false);
        handleFilterReset();
    };

    const handleFilterReset = () => {
        setFilterSelectedRegion(null);
        setFilterCountries(allCountries);
        setFilterCountry('GB');
        setSelectedProviders([]);
        setSelectedDurations([]);
        setSelectedDataAmounts([]);
    };

    const updateCatalogPrices = useCallback(async (catalogList) => {
        updateWholesalePrices(catalogList, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                getCatalogs();
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Wholesale prices updated successfully' });
            } else {
                const error = response.error;
                toast.current?.show({ severity: error?.severity || 'error', summary: 'Error', detail: (error?.errorMsg) ? error.errorMsg : error?.summary });
            }
        });
    }, [dispatch, headers, getCatalogs]);

    const onProviderPriceOverrideEdit = (rowData, overrideValue) => {
        const catalogKey = rowData.catalogId || rowData.name;
        setEditCatalogs(prevState => {
            const currentCatalog = prevState[catalogKey] || createEditCatalogItem(rowData);
            const updatedCatalog = {
                ...currentCatalog,
                editProviderPriceOverride: (overrideValue !== null && overrideValue !== undefined && overrideValue !== "")
                    ? utils.setNumberPrecision(overrideValue)
                    : null
            };
            return {
                ...prevState,
                [catalogKey]: updatedCatalog
            };
        });
    };

    const priceBodyTemplate = (rowData) => {
        const ourPrice = rowData?.ourPrice;
        return (
            <span>{(ourPrice !== undefined && ourPrice !== null && +ourPrice !== -1 && +ourPrice >= 0) ? utils.formatPriceCurrency(rowData?.priceCurrency, ourPrice) : "-"}</span>
        );
    };

    const [focusedCatalogId, setFocusedCatalogId] = useState(null);

    const providerPriceOverrideBodyTemplate = (rowData) => {
        const catalogKey = rowData.catalogId || rowData.name;
        const catalog = editCatalogs[catalogKey] || {};
        const editOverride = catalog.editProviderPriceOverride !== undefined ? catalog.editProviderPriceOverride : (rowData.providerPriceOverride ?? null);
        const hasValue = editOverride !== null && editOverride !== undefined && editOverride !== "";
        const showCurrency = canManagePrices && hasValue;

        return (
            <div className="list-price provider-price-override">
                {showCurrency && <span className="currencyVal">{globalConfig.defaultCurrency}</span>}
                <PR.InputNumber
                    value={editOverride}
                    onKeyDown={utils.blockInvalidChar}
                    onChange={(e) => onProviderPriceOverrideEdit(rowData, e.value)}
                    onFocus={() => setFocusedCatalogId(catalogKey)}
                    onBlur={() => setFocusedCatalogId(null)}
                    minFractionDigits={2}
                    disabled={!canManagePrices}
                />
            </div>
        );
    };

    const providerPriceBodyTemplate = (rowData) => {
        return (
            <span>{utils.formatPriceCurrency(rowData?.priceCurrency, rowData?.providerPrice)}</span>
        );
    };

    const calculatedPriceBodyTemplate = (rowData) => {
        const val = rowData?.calculatedPrice;
        return (
            <span>{(val !== undefined && val !== null && val !== "" && +val !== -1 && +val >= 0) ? utils.formatPriceCurrency(rowData?.priceCurrency, val) : "-"}</span>
        );
    };

    const minRetailPriceBodyTemplate = (rowData) => {
        return (
            <span>{rowData?.minRetailPrice ? utils.formatPriceCurrency(rowData?.priceCurrency, rowData?.minRetailPrice) : "-"}</span>
        );
    };

    const getUpdateHandler = (editCatalogs) => {
        return Object.values(editCatalogs).filter(e => {
            const origOverride = e.providerPriceOverride ?? null;
            const newOverride = (e.editProviderPriceOverride === "" || e.editProviderPriceOverride === undefined || e.editProviderPriceOverride === null) ? null : e.editProviderPriceOverride;
            return origOverride !== newOverride;
        });
    };

    const executePriceUpdate = (updatedCatalogData) => {
        if (updatedCatalogData.length > 0) {
            const catalogList = updatedCatalogData.map((uc) => {
                const overrideVal = (uc.editProviderPriceOverride === null || uc.editProviderPriceOverride === undefined || uc.editProviderPriceOverride === "")
                    ? null
                    : +uc.editProviderPriceOverride;
                return {
                    name: uc.name,
                    providerPriceOverride: overrideVal,
                };
            });
            updateCatalogPrices(catalogList);
        }
    };

    const updatePrice = () => {
        const updatedCatalogData = getUpdateHandler(editCatalogs);
        if (updatedCatalogData.length === 0) return;

        const hasLowerThanBuyPrice = updatedCatalogData.some(item => {
            const overrideVal = item.editProviderPriceOverride;
            const buyPriceVal = item.providerPrice;
            if (overrideVal === null || overrideVal === undefined || overrideVal === "") return false;
            if (buyPriceVal === null || buyPriceVal === undefined || buyPriceVal === "") return false;
            return +overrideVal < +buyPriceVal;
        });

        if (hasLowerThanBuyPrice) {
            toast.current?.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Override price should not be less than Buy price',
                life: 4000
            });
            return;
        }

        const hasLowerThanMRP = updatedCatalogData.some(item => {
            const overrideVal = item.editProviderPriceOverride;
            const mrpVal = item.minRetailPrice;
            if (overrideVal === null || overrideVal === undefined || overrideVal === "") return false;
            if (mrpVal === null || mrpVal === undefined || mrpVal === "") return false;
            return +overrideVal < +mrpVal;
        });

        if (hasLowerThanMRP) {
            PR.confirmDialog({
                message: (
                    <>
                        <p style={{ margin: "0px 0px 5px" }}>Some of the override price is less than MRP.</p>
                        <p style={{ margin: "0px" }}>Are you sure you want to proceed with the update?</p>
                    </>
                ),
                header: 'Warning',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Proceed',
                rejectLabel: 'Cancel',
                acceptClassName: 'p-button-danger',
                accept: () => executePriceUpdate(updatedCatalogData),
                reject: () => { }
            });
        } else {
            executePriceUpdate(updatedCatalogData);
        }
    };

    const exportCSV = (e) => {
        setLoading(true);
        e.preventDefault();
        const defaultFilters = {
            sortingOrder: 'asc',
            sortingField: 'providerprice',
        };
        const queryParams = utils.serializeParams({ ...defaultFilters });
        getWholesaleList(queryParams, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                setLoading(false);
                const data = response.data ? response.data : [];
                const requiredKeys = ['country', 'iso', 'providerName', 'description', 'name', 'providerPrice', 'calculatedPrice', 'providerPriceOverride', 'minRetailPrice', 'ourPrice'];
                const exportArray = utils.extractRequiredKeys(data, requiredKeys);
                if (exportArray && exportArray.length > 0) {
                    const capitalizeFirstLetter = (str) => {
                        if (str === 'providerPrice') {
                            return 'Buy Price';
                        } else if (str === 'calculatedPrice') {
                            return 'Calculated Price';
                        } else if (str === 'providerPriceOverride') {
                            return 'Provider Price Override';
                        } else if (str === 'ourPrice') {
                            return 'List Price';
                        } else if (str === 'providerName') {
                            return 'Provider';
                        } else if (str === 'description') {
                            return 'Plan';
                        } else if (str === 'minRetailPrice') {
                            return 'Min Retail Price';
                        } else {
                            return str.charAt(0).toUpperCase() + str.slice(1);
                        }
                    };
                    const csvContent = "data:text/csv;charset=utf-8," +
                        Object.keys(exportArray[0]).map(capitalizeFirstLetter).join(",") + "\n" +
                        exportArray.map(entry => {
                            return Object.values(entry).map(value => {
                                if (typeof value === 'string' && (value.includes(' ') || value.includes(','))) {
                                    return `"${value}"`;
                                }
                                return value;
                            }).join(",");
                        }).join("\n");
                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", `bo.${appName}-wholesale-bundle-prices-${timeStamp}.csv`);
                    document.body.appendChild(link);
                    link.click();
                }
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Exported successfully' });
            } else {
                setLoading(false);
                const error = response?.error;
                toast.current?.show({ severity: error?.severity || 'error', summary: 'Error', detail: (error?.errorMsg) ? error?.errorMsg : error?.summary });
            }
        });
    };

    const rowClassName = (rowData) => {
        let classNames = '';
        const catalogKey = rowData.catalogId || rowData.name;
        const catalog = editCatalogs[catalogKey] || {};
        const origOverride = rowData.providerPriceOverride ?? null;
        const newOverride = (catalog.editProviderPriceOverride === "" || catalog.editProviderPriceOverride === undefined || catalog.editProviderPriceOverride === null) ? null : catalog.editProviderPriceOverride;
        if (origOverride !== newOverride) {
            classNames += ' row-edited';
        }
        const buyPrice = rowData.providerPrice;
        if (newOverride !== null && buyPrice !== null && buyPrice !== undefined && +newOverride < +buyPrice) {
            classNames += ' row-invalid-price';
        }
        return classNames;
    };

    useEffect(() => {
        getPriceFilters(headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                setDataAmountList(response.data?.dataAmountForDisplay?.length > 0 ? [...new Set(response.data?.dataAmountForDisplay)] : []);
                setDurationList(response.data?.duration?.length > 0 ? [...new Set(response.data?.duration)] : []);
                const providers = response.data?.providers?.length > 0 ? ['All', ...new Set(response.data?.providers)] : [];
                setProviderList(providers);
            } else {
                const error = response?.error;
                toast.current?.show({ severity: error?.severity || 'error', summary: 'Error', detail: (error?.errorMsg) ? error?.errorMsg : error?.summary });
            }
        });
    }, [dispatch, headers]);

    const handleProviderChange = (e, provider) => {
        if (provider === 'All') {
            if (e.checked) {
                setSelectedProviders(providerList);
            } else {
                setSelectedProviders([]);
            }
        } else {
            const newSelectedProviders = e.checked
                ? [...selectedProviders, provider]
                : selectedProviders.filter((p) => p !== provider);

            if (newSelectedProviders.length === providerList.length - 1 && !newSelectedProviders.includes('All')) {
                setSelectedProviders([...newSelectedProviders, 'All']);
            } else {
                setSelectedProviders(newSelectedProviders.filter((p) => p !== 'All'));
            }
        }
    };

    const handleDurationChange = (e, duration) => {
        const newSelectedDurations = e.checked
            ? [...selectedDurations, duration]
            : selectedDurations.filter((d) => d !== duration);
        setSelectedDurations(newSelectedDurations);
    };

    const handleDataAmountChange = (e, data) => {
        const newSelectedDataAmounts = e.checked
            ? [...selectedDataAmounts, data]
            : selectedDataAmounts.filter((d) => d !== data);
        setSelectedDataAmounts(newSelectedDataAmounts);
    };


    const descriptionTemplate = (rowData) => {
        return (
            <div className="flex flex-column gap-1">
                <div className="flex align-items-center">
                    {rowData?.description}
                    {rowData?.dataAmountForDisplay?.toLowerCase() === "unlimited" && rowData?.fup &&
                        <i
                            className="pi pi-info-circle ml-2"
                            style={{ cursor: "pointer" }}
                            title={`FUP: ${rowData?.fup}`}
                        />
                    }
                    <RoamingColumn data={rowData} showCount={false} />
                </div>
                <AllowanceInfo
                    allowances={rowData?.allowances}
                    dataAmountForDisplay={rowData?.dataAmountForDisplay}
                    fup={rowData?.fup}
                />
            </div>
        );
    };

    return (
        <>
            <div className="main wholesale-price-adjustment">
                <div className="layout-sidebar">
                    <AdminHeader />
                </div>
                <PR.ConfirmDialog />
                <div className="layout-content-wrapper">
                    <section className="admin-users-section price-adjust-section">
                        <div className="grid grid-nogutter">
                            <div className="col-12">
                                <div className="heading-sec">
                                    <div className="flex align-items-center justify-content-between mobile-flex">
                                        <div className="flex align-items-center justify-content-between width-full">
                                            <h1>Wholesale Price Adjustment</h1>
                                            <PR.Button text className="filter-toggle-button" onClick={toggleFilter}>
                                                <i className={`pi ${filterOpen ? "pi-filter-slash" : "pi-filter-fill"}`}></i>
                                            </PR.Button>
                                        </div>
                                        <div className="right-section flex align-items-center gap-3">
                                            <PR.Button label="Export CSV" className="export-button" icon="pi pi-file-export" iconPos="left" onClick={exportCSV} disabled={loading || !canManagePrices} />
                                        </div>
                                    </div>
                                    <div className={`filter-section flex align-items-center justify-content-between mt-5 ${filterOpen ? "mobile-filter-section" : ""}`}>
                                        <div className="flex gap-3 align-items-center mobile-lex">
                                            <PR.Dropdown value={selectedRegion} onChange={(e) => handleRegionSelection(e.value)} options={uniqueRegions} optionLabel="label" placeholder="All Regions" filter className="w-full md:w-14rem" disabled={loading}
                                                showClear={selectedRegion ? true : false} />
                                            <PR.Dropdown
                                                filter
                                                disabled={loading}
                                                value={country}
                                                options={countries}
                                                onChange={handleCountrySelection}
                                                resetFilterOnHide
                                                optionLabel="name"
                                                optionValue="iso"
                                                placeholder="All Countries"
                                            />
                                        </div>
                                        <div className="flex align-items-center gap-3">
                                            <div className="flex">
                                                <PR.Checkbox
                                                    name="showOnlyOverridden"
                                                    onChange={(e) => setShowOnlyOverridden(Boolean(e.checked))}
                                                    checked={showOnlyOverridden}
                                                />
                                                <label className="ml-2">Show only override prices</label>
                                            </div>
                                            <PR.Button label="Filter" className="exportBtn" icon="pi pi-filter" onClick={() => setFiltersVisible(true)} disabled={loading} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="users-data-table price-adjustment plan-selection card whole-sale-table">
                            <PR.DataTable
                                loading={loading}
                                editMode="row"
                                value={displayedCatalogs}
                                rowClassName={rowClassName}
                                let-i="rowIndex"
                                sortField={sortField}
                                sortOrder={sortOrder}
                                className="price-adjustment-table"
                                emptyMessage={<span className="text-gray-500">No results found</span>}
                                onSort={(e) => {
                                    const sortDirection = e.sortOrder === 1 ? "asc" : "desc";
                                    const sortBy = e.sortField;
                                    setSortField(sortBy);
                                    setSortOrder(e.sortOrder);
                                    getCatalogs(sortBy, sortDirection);
                                }}
                            >
                                <PR.Column field="providerName" header="Provider" style={{ width: "8%" }}></PR.Column>
                                <PR.Column field="description" body={descriptionTemplate} header="Plan" style={{ width: "12%" }} className="plan-column" sortable />
                                <PR.Column body={(rowData) => (
                                    <div>
                                        <div><span className="bundle-column">{rowData.name ? rowData.name : "-"}</span></div>
                                        <div style={{ marginTop: "4px" }}>
                                            {rowData.groups ? rowData.groups : ""}
                                        </div>
                                    </div>
                                )}
                                    header="Bundle Name" field="name" style={{ width: "14%" }} sortable>
                                </PR.Column>
                                <PR.Column field="dataAmount" header="Data" style={{ width: "6%" }} body={setDataAmount} sortable />
                                <PR.Column field="duration" header="Duration" style={{ width: "7%" }} body={setDataDuration} />
                                <PR.Column field="speed" header="Speed" style={{ width: "10%" }} body={setDataSpeed} className="speed-column" />
                                <PR.Column field="providerPrice" header="Buy Price" style={{ width: "10%" }} body={providerPriceBodyTemplate} sortable />
                                <PR.Column field="calculatedPrice" header="Calc Price" style={{ width: "10%" }} body={calculatedPriceBodyTemplate} sortable />
                                <PR.Column field="providerPriceOverride" header="Override Price" style={{ width: "11%" }} body={providerPriceOverrideBodyTemplate} />
                                <PR.Column field="minRetailPrice" header="MRP" style={{ width: "7%" }} body={minRetailPriceBodyTemplate} sortable />
                                <PR.Column field="ourPrice" header="List Price" style={{ width: "10%" }} className="last-column" body={priceBodyTemplate} sortable />
                            </PR.DataTable>
                        </div>
                        <div className="buttons-sections flex align-items-center justify-content-end gap-3">
                            <PR.Button label="Reset" className="confirm-button reset-btn" onClick={resetData} disabled={!canManagePrices} />
                            <PR.Button label="Confirm" className="confirm-button"
                                onClick={() => updatePrice()}
                                disabled={getUpdateHandler(editCatalogs).length === 0 || !canManagePrices} />
                        </div>
                    </section>

                    <PR.Dialog header="Filter" visible={filtersVisible} onHide={() => { if (!filtersVisible) return; setFiltersVisible(false); }} draggable={false} blockScroll={true} className="filter-dialog">
                        <form autoComplete="off" className="mt-0" onSubmit={handleFilterSubmit}>
                            <div className="flex gap-3 align-items-center flex-wrap">
                                <PR.Dropdown value={filterSelectedRegion} onChange={(e) => regionFilterHandler(e.value)} options={uniqueRegions} optionLabel="label" placeholder="All Regions" filter className="w-full md:w-14rem" disabled={loading} resetFilterOnHide showClear="true" />
                                <PR.Dropdown
                                    filter
                                    disabled={loading}
                                    value={filterCountry}
                                    options={filterCountries}
                                    onChange={countryFilterHandler}
                                    resetFilterOnHide
                                    optionLabel="name"
                                    optionValue="iso"
                                    placeholder="All Countries"
                                />
                            </div>
                            {providerList.length > 0 && <div className="card mt-4">
                                <p>By list of providers</p>
                                <div className="flex gap-5 flex-wrap">
                                    {
                                        providerList.length > 0 && providerList.map((provider, index) =>
                                            <div className="flex" key={index}>
                                                <PR.Checkbox inputId={`provider${index}`} checked={selectedProviders.includes(provider)} onChange={(e) => handleProviderChange(e, provider)}></PR.Checkbox>
                                                <label htmlFor="provider" className="ml-2">{provider}</label>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>}
                            {durationList.length > 0 && <div className="card mt-4">
                                <p>By plan duration</p>
                                <div className="flex gap-5 flex-wrap">
                                    {
                                        durationList.length > 0 && durationList.map((duration, index) =>
                                            <div className="flex" key={index}>
                                                <PR.Checkbox inputId={`duration${index}`} checked={selectedDurations.includes(duration)} onChange={(e) => handleDurationChange(e, duration)}></PR.Checkbox>
                                                <label htmlFor="duration" className="ml-2">{duration} {duration > 1 ? 'days' : 'day'}</label>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>}
                            {dataAmountList.length > 0 && <div className="card mt-4">
                                <p>By amount of data</p>
                                <div className="flex gap-5 amount-data-options flex-wrap">
                                    {
                                        dataAmountList.length > 0 && dataAmountList.map((data, index) =>
                                            <div className="flex" key={index}>
                                                <PR.Checkbox inputId={`data${index}`} checked={selectedDataAmounts.includes(data)} onChange={(e) => handleDataAmountChange(e, data)}></PR.Checkbox>
                                                <label htmlFor="amount" className="ml-2">{data}</label>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>}
                            <div className="buttons-sections flex align-items-center justify-content-end gap-3">
                                <PR.Button label="Reset" type="button" className="confirm-button reset-btn min-width" onClick={handleFilterReset} disabled={!isChanged} />
                                <PR.Button label="Apply" type="submit" className="confirm-button min-width" />
                            </div>
                        </form>
                    </PR.Dialog>
                    <PR.Toast ref={toast} position='top-right' />
                    <AdminFooter />
                </div>
            </div>
        </>
    );
};

export default WholesalePriceAdjustment;
